import type { TFunction } from "i18next";
import { ApiError, type TimesheetStatus } from "@/lib/api";

function detailString(error: ApiError, key: string): string | undefined {
  const value = error.details?.[key];
  return typeof value === "string" ? value : undefined;
}

function validationErrors(error: ApiError): string[] {
  const value = error.details?.errors;
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

export function getTimekeepingErrorMessage(error: unknown, t: TFunction): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error && error.message
      ? error.message
      : t("timekeeping.errors.generic", "Something went wrong. Please try again.");
  }

  const status = detailString(error, "currentStatus") as TimesheetStatus | undefined;
  if (error.code === "TIMESHEET_NOT_AWAITING_REVIEW") {
    if (status === "Approved") return t("timekeeping.errors.alreadyApproved", "The timesheet is already approved.");
    if (status === "Rejected") return t("timekeeping.errors.alreadyRejected", "The timesheet is already rejected.");
    if (status === "Needs Changes") return t("timekeeping.errors.alreadyReturned", "The timesheet was already returned for changes.");
    if (status === "Locked") return t("timekeeping.errors.alreadyLocked", "The timesheet is locked and can no longer be reviewed.");
    return t("timekeeping.errors.notAwaitingReview", "The timesheet is no longer awaiting review.");
  }

  if (error.code === "TIMESHEET_REVIEW_ALREADY_RECORDED") {
    return t("timekeeping.errors.reviewAlreadyRecorded", "Your review action has already been recorded.");
  }

  // Backward-compatible handling while older server instances may still return
  // the pre-code English message during a rolling deployment.
  const legacyReviewStatus = error.message.match(/^Timesheet is (.+), not awaiting review$/i)?.[1];
  if (legacyReviewStatus) {
    if (/approved/i.test(legacyReviewStatus)) return t("timekeeping.errors.alreadyApproved", "The timesheet is already approved.");
    if (/rejected/i.test(legacyReviewStatus)) return t("timekeeping.errors.alreadyRejected", "The timesheet is already rejected.");
    if (/needs changes/i.test(legacyReviewStatus)) return t("timekeeping.errors.alreadyReturned", "The timesheet was already returned for changes.");
    if (/locked/i.test(legacyReviewStatus)) return t("timekeeping.errors.alreadyLocked", "The timesheet is locked and can no longer be reviewed.");
    return t("timekeeping.errors.notAwaitingReview", "The timesheet is no longer awaiting review.");
  }

  const errors = validationErrors(error);
  const dailyLimit = Number(error.details?.maxDailyHours);
  if (errors.some((item) => item.startsWith("over_daily_max:")) && Number.isFinite(dailyLimit)) {
    return t("timekeeping.errors.dailyLimit", {
      limit: dailyLimit,
      defaultValue: "You cannot enter more than {{limit}} working hours in one day.",
    });
  }
  if (errors.some((item) => item.startsWith("future_date:"))) {
    return t("timekeeping.errors.futureDate", "Future-date entries are disabled in the timekeeping settings.");
  }
  if (errors.some((item) => item.startsWith("before_start:") || item.startsWith("after_end:"))) {
    return t("timekeeping.errors.outsideEmployment", "Time can only be entered within the employee's employment period.");
  }
  if (errors.some((item) => item.startsWith("out_of_week:"))) {
    return t("timekeeping.errors.outsideWeek", "An entry falls outside the selected week.");
  }
  if (error.code === "incomplete_week") {
    return t("timekeeping.errors.incompleteWeek", "Enter time for every required working day before submitting the week.");
  }

  const rawKnownMessages: Record<string, string> = {
    "Validation failed": t("timekeeping.errors.validation", "Check the entered hours and try again."),
    "Failed to approve timesheet": t("timekeeping.errors.approvalFailed", "The timesheet could not be approved. Please try again."),
    "Failed to reject timesheet": t("timekeeping.errors.rejectionFailed", "The timesheet could not be rejected. Please try again."),
    "Failed to return timesheet": t("timekeeping.errors.returnFailed", "The timesheet could not be returned for changes. Please try again."),
    "Failed to save timesheet": t("timekeeping.errors.saveFailed", "The timesheet could not be saved. Please try again."),
    "Failed to save entries": t("timekeeping.errors.saveFailed", "The timesheet could not be saved. Please try again."),
  };
  return rawKnownMessages[error.message]
    ?? error.message
    ?? t("timekeeping.errors.generic", "Something went wrong. Please try again.");
}

export function isTerminalReviewConflict(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  const status = detailString(error, "currentStatus");
  return error.code === "TIMESHEET_REVIEW_ALREADY_RECORDED"
    || /^Timesheet is (Approved|Rejected|Needs Changes|Locked), not awaiting review$/i.test(error.message)
    || status === "Approved"
    || status === "Rejected"
    || status === "Needs Changes"
    || status === "Locked";
}
